/**
 * @fileoverview Settings API Service
 * @description API service for system settings endpoints
 * @module @xtream/frontend/services/api/settings
 *
 * Endpoints:
 * - GET /settings - Get all settings
 * - GET /settings/branding - Get branding settings
 * - PUT /settings/branding - Update branding settings
 * - GET /settings/theme - Get theme settings
 * - PUT /settings/theme - Update theme settings
 */

import { apiClient, ApiError } from './client';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface BrandingSettings {
  panelName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
}

export interface ThemeColors {
  'corporate-pink': string;
  'corporate-gray-light': string;
  'corporate-gray-dark': string;
  'corporate-white': string;
}

export interface ThemeSettings {
  colors: ThemeColors;
}

export interface LoggingSettings {
  verboseLogging: boolean;
}

export interface AllSettings {
  branding: BrandingSettings;
  theme: ThemeSettings;
  logging: LoggingSettings;
}

export interface UpdateBrandingDTO {
  panelName?: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
}

export interface UpdateThemeDTO {
  colors?: Partial<ThemeColors>;
}

export interface UpdateLoggingDTO {
  verboseLogging?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT VALUES
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_BRANDING: BrandingSettings = {
  panelName: 'XtreamTranscoder',
  logoUrl: null,
  faviconUrl: null,
};

export const DEFAULT_THEME: ThemeSettings = {
  colors: {
    'corporate-pink': '#ff2b76',
    'corporate-gray-light': '#21242a',
    'corporate-gray-dark': '#16181C',
    'corporate-white': '#ffffff',
  },
};

export const DEFAULT_LOGGING: LoggingSettings = {
  verboseLogging: false,
};

// ═══════════════════════════════════════════════════════════════════════════
// SETTINGS API SERVICE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Settings API service
 */
export const settingsApi = {
  /**
   * Get all settings
   */
  async getAll(): Promise<AllSettings> {
    try {
      const response = await apiClient.get<AllSettings>('/settings', { skipAuth: true });

      if (!response.success || !response.data) {
        // Return defaults if API fails
        return {
          branding: DEFAULT_BRANDING,
          theme: DEFAULT_THEME,
          logging: DEFAULT_LOGGING,
        };
      }

      return response.data;
    } catch {
      // Return defaults on error
      return {
        branding: DEFAULT_BRANDING,
        theme: DEFAULT_THEME,
        logging: DEFAULT_LOGGING,
      };
    }
  },

  /**
   * Get branding settings
   */
  async getBranding(): Promise<BrandingSettings> {
    try {
      const response = await apiClient.get<BrandingSettings>('/settings/branding', {
        skipAuth: true,
      });

      if (!response.success || !response.data) {
        return DEFAULT_BRANDING;
      }

      return response.data;
    } catch {
      return DEFAULT_BRANDING;
    }
  },

  /**
   * Update branding settings
   */
  async updateBranding(data: UpdateBrandingDTO): Promise<BrandingSettings> {
    const response = await apiClient.put<BrandingSettings>('/settings/branding', data);

    if (!response.success || !response.data) {
      throw new ApiError(
        response.error?.message || 'Failed to update branding',
        response.error?.code || 'UPDATE_BRANDING_FAILED',
        400,
      );
    }

    return response.data;
  },

  /**
   * Get theme settings
   */
  async getTheme(): Promise<ThemeSettings> {
    try {
      const response = await apiClient.get<ThemeSettings>('/settings/theme', { skipAuth: true });

      if (!response.success || !response.data) {
        return DEFAULT_THEME;
      }

      return response.data;
    } catch {
      return DEFAULT_THEME;
    }
  },

  /**
   * Update theme settings
   */
  async updateTheme(data: UpdateThemeDTO): Promise<ThemeSettings> {
    const response = await apiClient.put<ThemeSettings>('/settings/theme', data);

    if (!response.success || !response.data) {
      throw new ApiError(
        response.error?.message || 'Failed to update theme',
        response.error?.code || 'UPDATE_THEME_FAILED',
        400,
      );
    }

    return response.data;
  },

  /**
   * Get logging settings
   */
  async getLogging(): Promise<LoggingSettings> {
    try {
      const response = await apiClient.get<LoggingSettings>('/settings/logging', {
        skipAuth: true,
      });

      if (!response.success || !response.data) {
        return DEFAULT_LOGGING;
      }

      return response.data;
    } catch {
      return DEFAULT_LOGGING;
    }
  },

  /**
   * Update logging settings
   */
  async updateLogging(data: UpdateLoggingDTO): Promise<LoggingSettings> {
    const response = await apiClient.put<LoggingSettings>('/settings/logging', data);

    if (!response.success || !response.data) {
      throw new ApiError(
        response.error?.message || 'Failed to update logging settings',
        response.error?.code || 'UPDATE_LOGGING_FAILED',
        400,
      );
    }

    return response.data;
  },
};

export default settingsApi;
